import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = '/api';

interface Session {
  id: number;
  device_name?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  last_seen: string;
  is_active: boolean;
}

function badge(color: string, text: string) {
  return (
    <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
      {text}
    </span>
  );
}

function timeSince(ts: string) {
  if (!ts) return '-';
  const d = Date.now() - new Date(ts).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function SecurityPage({ user }: { user: any }) {
  const isSuperAdmin = user?.role === 'super_admin' || !!user?.is_admin;
  const [tab, setTab] = useState<'sessions' | 'blacklist' | 'debug'>('sessions');

  // ── Active Sessions ──────────────────────────────────────────────────────────
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessLoading, setSessLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [sessError, setSessError] = useState('');
  const [sessSuccess, setSessSuccess] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin-panel/users`);
      setAllUsers(Array.isArray(r.data) ? r.data : r.data?.users || []);
    } catch { setAllUsers([]); }
  }, []);

  const loadSessions = useCallback(async (userId: number) => {
    setSessLoading(true); setSessError('');
    try {
      const r = await axios.get(`${API}/admin/users/${userId}/sessions`);
      setSessions(Array.isArray(r.data) ? r.data.filter((s: Session) => s.is_active) : []);
    } catch { setSessError('Failed to load sessions'); }
    setSessLoading(false);
  }, []);

  useEffect(() => { if (tab === 'sessions') loadUsers(); }, [tab, loadUsers]);

  const revokeAll = async (userId: number) => {
    setRevoking(true); setSessError(''); setSessSuccess('');
    try {
      const r = await axios.post(`${API}/admin/users/${userId}/sessions/revoke`, {});
      setSessSuccess(`Revoked ${r.data?.revoked ?? 0} session(s). User will be logged out on next request.`);
      setSessions([]);
    } catch { setSessError('Revoke failed'); }
    setRevoking(false);
  };

  // ── Blacklist ─────────────────────────────────────────────────────────────────
  const [blacklist, setBlacklist] = useState<any[]>([]);
  const [blLoading, setBlLoading] = useState(false);
  const [blForm, setBlForm] = useState({ number: '', description: '' });
  const [blSaving, setBlSaving] = useState(false);
  const [blError, setBlError] = useState('');
  const [blSuccess, setBlSuccess] = useState('');

  const loadBlacklist = useCallback(async () => {
    setBlLoading(true);
    try {
      const r = await axios.get(`${API}/asterisk/blacklist`);
      setBlacklist(Array.isArray(r.data) ? r.data : []);
    } catch { setBlError('Failed to load blacklist'); }
    setBlLoading(false);
  }, []);

  useEffect(() => { if (tab === 'blacklist') loadBlacklist(); }, [tab, loadBlacklist]);

  const addBlacklist = async () => {
    if (!blForm.number.trim()) return;
    setBlSaving(true); setBlError(''); setBlSuccess('');
    try {
      await axios.post(`${API}/asterisk/blacklist`, blForm);
      setBlSuccess('Number added to blacklist');
      setBlForm({ number: '', description: '' });
      await loadBlacklist();
    } catch (e: any) { setBlError(e?.response?.data?.error || 'Failed to add'); }
    setBlSaving(false);
  };

  const removeBlacklist = async (id: number) => {
    try {
      await axios.delete(`${API}/asterisk/blacklist/${id}`);
      setBlacklist(bl => bl.filter(b => b.id !== id));
      setBlSuccess('Number unblocked');
    } catch { setBlError('Remove failed'); }
  };

  const tabStyle = (t: string) => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
    fontSize: 13, transition: 'all 0.15s',
    background: tab === t ? '#4f46e5' : 'transparent',
    color: tab === t ? '#fff' : '#9ca3af',
  });


  // ── Debug Access Token ───────────────────────────────────────────────────────
  const [debugToken, setDebugToken]   = useState<any>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError]   = useState('');
  const [debugSuccess, setDebugSuccess] = useState('');
  const [debugTtl, setDebugTtl]       = useState(2);
  const [showCode, setShowCode]       = useState(false);

  const loadDebugToken = useCallback(async () => {
    try {
      const r = await axios.get(API + '/admin/debug-token/current');
      setDebugToken(r.data);
    } catch { setDebugToken(null); }
  }, []);

  useEffect(() => { if (tab === 'debug') loadDebugToken(); }, [tab, loadDebugToken]);

  const generateDebugToken = async () => {
    setDebugLoading(true); setDebugError(''); setDebugSuccess('');
    try {
      const r = await axios.post(API + '/admin/debug-token/generate', {
        label: 'Debug access', ttl_hours: debugTtl,
      });
      setDebugToken({ active: true, is_valid: true, ...r.data });
      setDebugSuccess('New debug code generated successfully.');
      setShowCode(true);
    } catch (e: any) {
      setDebugError(e.response?.data?.error || 'Failed to generate code');
    }
    setDebugLoading(false);
  };

  return (
    <>
      <div className="admin-page-title">Security</div>
      <div className="admin-page-subtitle">
        Manage active user sessions, call number blacklist, and access control
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#1e2538', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        <button style={tabStyle('sessions')} onClick={() => setTab('sessions')}>Active Sessions</button>
        <button style={tabStyle('blacklist')} onClick={() => setTab('blacklist')}>Call Blacklist</button>
        {(user?.is_admin || user?.role === 'tenant_admin' || user?.role === 'super_admin') && (
          <button style={tabStyle('debug')} onClick={() => setTab('debug')}>Debug Access</button>
        )}
      </div>

      {/* ── Active Sessions ── */}
      {tab === 'sessions' && (
        <div className="content-card">
          <h3 style={{ marginTop: 0 }}>Active User Sessions</h3>
          <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
            Select a user to view their active sessions and optionally force-logout them from all devices.
          </p>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <select
              style={{ padding: '8px 12px', background: '#1e2538', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb', fontSize: 13, minWidth: 280 }}
              value={selectedUser ?? ''}
              onChange={e => {
                const uid = Number(e.target.value);
                setSelectedUser(uid || null);
                setSessions([]);
                setSessSuccess('');
                if (uid) loadSessions(uid);
              }}
            >
              <option value="">— Select a user —</option>
              {allUsers.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.username}{u.display_name ? ` (${u.display_name})` : ''} — {u.role}
                </option>
              ))}
            </select>
            {selectedUser && sessions.length > 0 && (
              <button
                onClick={() => revokeAll(selectedUser)}
                disabled={revoking}
                style={{ padding: '8px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                {revoking ? 'Revoking…' : `Force Logout (${sessions.length} session${sessions.length > 1 ? 's' : ''})`}
              </button>
            )}
          </div>

          {sessError && <div style={{ color: '#ef4444', marginBottom: 10, fontSize: 13 }}>{sessError}</div>}
          {sessSuccess && <div style={{ color: '#10b981', marginBottom: 10, fontSize: 13 }}>{sessSuccess}</div>}

          {sessLoading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32 }}>Loading sessions…</div>
          ) : !selectedUser ? (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 40, fontSize: 14 }}>Select a user above to view their sessions.</div>
          ) : sessions.length === 0 ? (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 40, fontSize: 14 }}>No active sessions for this user.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Device</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>IP Address</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Created</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Last Active</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s: Session) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: '8px' }}>{s.device_name || '—'}</td>
                    <td style={{ padding: '8px', fontFamily: 'monospace', color: '#60a5fa' }}>{s.ip_address || '—'}</td>
                    <td style={{ padding: '8px', color: '#9ca3af' }}>{new Date(s.created_at).toLocaleString()}</td>
                    <td style={{ padding: '8px' }}>{timeSince(s.last_seen)}</td>
                    <td style={{ padding: '8px' }}>{badge('#10b981', 'Active')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 20, padding: 14, background: '#0f172a', borderRadius: 8, fontSize: 12, color: '#6b7280' }}>
            <strong style={{ color: '#94a3b8' }}>Note:</strong> Revoking sessions sends an immediate force-logout event.
            The user is signed out on their next API call (typically within seconds).
          </div>
        </div>
      )}

      {/* ── Blacklist Tab ── */}
      {tab === 'blacklist' && (
        <div className="content-card">
          <h3 style={{ marginTop: 0 }}>Call Number Blacklist</h3>
          <p style={{ color: '#9ca3af', fontSize: 13 }}>
            Block inbound calls from specific numbers. Blacklisted callers receive a rejection or busy tone.
          </p>

          {blError && <div style={{ color: '#ef4444', marginBottom: 10, fontSize: 13 }}>{blError}</div>}
          {blSuccess && <div style={{ color: '#10b981', marginBottom: 10, fontSize: 13 }}>{blSuccess}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              value={blForm.number}
              onChange={e => setBlForm(f => ({ ...f, number: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && addBlacklist()}
              placeholder="Phone number (e.g. +971501234567)"
              style={{ flex: '0 0 220px', padding: '8px 12px', background: '#1e2538', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb', fontSize: 13 }}
            />
            <input
              value={blForm.description}
              onChange={e => setBlForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Reason / note (optional)"
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: '#1e2538', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb', fontSize: 13 }}
            />
            <button
              onClick={addBlacklist}
              disabled={blSaving || !blForm.number.trim()}
              style={{ padding: '8px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              {blSaving ? 'Adding…' : '🚫 Block Number'}
            </button>
          </div>

          {blLoading ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32 }}>Loading…</div>
          ) : blacklist.length === 0 ? (
            <div style={{ color: '#6b7280', textAlign: 'center', padding: 40, fontSize: 14 }}>No blocked numbers configured.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Number</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Reason/Note</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Blocked On</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {blacklist.map((b: any) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', color: '#f87171', fontWeight: 700 }}>{b.number}</td>
                    <td style={{ padding: '8px', color: '#9ca3af' }}>{b.description || '—'}</td>
                    <td style={{ padding: '8px', color: '#6b7280' }}>{new Date(b.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <button
                        onClick={() => removeBlacklist(b.id)}
                        style={{ padding: '4px 12px', background: 'transparent', color: '#ef4444', border: '1px solid #ef444466', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                      >
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'debug' && (
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>Debug Access Token</h3>
            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
              Generate a time-limited code that allows users to unlock the diagnostics log panel
              in the web and desktop clients. Share the code verbally or via secure channel — never via chat.
            </p>
          </div>

          {debugError && <div style={{ background: '#ef444420', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{debugError}</div>}
          {debugSuccess && <div style={{ background: '#10b98120', border: '1px solid #10b981', borderRadius: 8, padding: '10px 14px', color: '#10b981', fontSize: 13, marginBottom: 12 }}>{debugSuccess}</div>}

          {debugToken?.active && debugToken?.is_valid && (
            <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Active Code</span>
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  Expires {new Date(debugToken.expires_at).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <code style={{
                  flex: 1, fontFamily: 'monospace', fontSize: 22, fontWeight: 700, letterSpacing: 6,
                  color: showCode ? '#C9A84C' : 'transparent', background: showCode ? '#111827' : '#374151',
                  padding: '10px 16px', borderRadius: 8, textAlign: 'center', display: 'block',
                  filter: showCode ? 'none' : 'blur(0)',
                  border: '1px solid #374151', userSelect: showCode ? 'text' : 'none',
                }}>
                  {showCode ? debugToken.code : '●●●●●●●●●●'}
                </code>
                <button
                  onClick={() => setShowCode(v => !v)}
                  style={{ padding: '8px 14px', background: '#374151', border: '1px solid #4b5563', borderRadius: 7, color: '#9ca3af', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  {showCode ? '🙈 Hide' : '👁 Reveal'}
                </button>
                {showCode && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(debugToken.code); setDebugSuccess('Code copied to clipboard.'); }}
                    style={{ padding: '8px 14px', background: '#374151', border: '1px solid #4b5563', borderRadius: 7, color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}
                  >
                    📋 Copy
                  </button>
                )}
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 11, color: '#6b7280' }}>
                ⚠️ Share this code only with support staff who need to view diagnostic logs. Generating a new code immediately invalidates this one.
              </p>
            </div>
          )}

          {(!debugToken?.active || !debugToken?.is_valid) && (
            <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>No active debug code. Generate one below.</p>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>Valid for</label>
              <select
                value={debugTtl}
                onChange={e => setDebugTtl(Number(e.target.value))}
                style={{ padding: '7px 10px', background: '#1f2937', border: '1px solid #374151', borderRadius: 7, color: '#e2e8f0', fontSize: 13 }}
              >
                <option value={1}>1 hour</option>
                <option value={2}>2 hours</option>
                <option value={4}>4 hours</option>
                <option value={8}>8 hours</option>
                <option value={24}>24 hours</option>
              </select>
            </div>
            <button
              onClick={generateDebugToken}
              disabled={debugLoading}
              style={{ padding: '9px 20px', background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: debugLoading ? 'not-allowed' : 'pointer', opacity: debugLoading ? 0.7 : 1 }}
            >
              {debugLoading ? 'Generating…' : debugToken?.active ? '🔄 Regenerate Code' : '🔐 Generate Debug Code'}
            </button>
          </div>

          <div style={{ marginTop: 24, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 14 }}>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 1 }}>How it works</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
              <li>Generate a code and share it with the user who needs to view logs</li>
              <li>User clicks the diagnostics (📋) button in the app and enters the code</li>
              <li>The code grants access to the log panel for the duration you set</li>
              <li>Generating a new code immediately invalidates the previous one</li>
              <li>The code is verified server-side — it cannot be guessed or bypassed</li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
